import { useCallback, useEffect, useMemo, useState } from 'react';

import { Preference } from '../../../../domain/preferences';
import { useGroupedMetrics, useMetricsLabelsQuery } from '../../../../features/preferences/useMetricsLabelsQuery';
import { useMetricsPreferencesMutation } from '../../../../features/preferences/useMetricsPreferencesMutation';
import { useMetricsPreferencesQuery } from '../../../../features/preferences/useMetricsPreferencesQuery';
import { arraysAreEqual } from '../../../../services/array';
import { usePreferencesTracking } from '../usePreferencesTracking';

export const useMetricsPreferences = (onClose: () => void) => {
  const { handleTrackApplyPreferences, handleTrackRemoveMetric, handleTrackAddMetric } = usePreferencesTracking();
  const { data: initiallySelectedMetrics, isLoading: isInitiallySelectedLoading } = useMetricsPreferencesQuery();
  const { data: labels, isLoading: isLabelsLoading } = useMetricsLabelsQuery();
  const { mutate, isPending: isMutationLoading } = useMetricsPreferencesMutation(onClose);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(initiallySelectedMetrics || []);
  const groupedMetrics = useGroupedMetrics();

  useEffect(() => {
    if (initiallySelectedMetrics) {
      if (initiallySelectedMetrics.length === 0 && labels) {
        setSelectedMetrics(labels.map((label) => label.metric));
      } else {
        setSelectedMetrics(initiallySelectedMetrics);
      }
    }
  }, [initiallySelectedMetrics, labels]);

  const handleMetricClick = useCallback((selectedMetric: string) => {
    setSelectedMetrics((prevSelectedMetrics) => {
      if (prevSelectedMetrics.includes(selectedMetric)) {
        handleTrackRemoveMetric(selectedMetric, prevSelectedMetrics.length - 1);
        return prevSelectedMetrics.filter((metric) => metric !== selectedMetric);
      }
      handleTrackAddMetric(selectedMetric, prevSelectedMetrics.length + 1);
      return [...prevSelectedMetrics, selectedMetric];
    });
  }, []);

  const handleSave = () => {
    handleTrackApplyPreferences({
      selectedPreference: Preference.metrics,
      metricsCount: selectedMetrics.length,
    });
    mutate(selectedMetrics);
  };

  const isLoading = useMemo(
    () => isInitiallySelectedLoading || isLabelsLoading,
    [isInitiallySelectedLoading, isLabelsLoading]
  );

  const isNoChangeInValues = useMemo(
    () =>
      arraysAreEqual(selectedMetrics, initiallySelectedMetrics) ||
      (initiallySelectedMetrics?.length === 0 &&
        arraysAreEqual(
          selectedMetrics,
          labels?.map((label) => label.metric)
        )),
    [selectedMetrics, initiallySelectedMetrics, labels]
  );

  const isSaveDisabled = useMemo(
    () => isLoading || selectedMetrics.length === 0 || isNoChangeInValues,
    [isLoading, selectedMetrics.length, isNoChangeInValues]
  );

  return {
    selectedMetrics,
    isLoading,
    isMutationLoading,
    handleSave,
    handleMetricClick,
    groupedMetrics,
    isSaveDisabled,
    isNoChangeInValues,
  };
};
